import React, {
  useState, useEffect, useRef, useCallback,
} from 'react';
import { useParams, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Container, ThemeProvider, Typography } from '@material-ui/core';
import { useTheme } from '@material-ui/core/styles';
import { usePostHog } from 'posthog-js/react';

import {
  ContentContainer,
  HeaderBox,
  Title,
  OtpInputContainer,
  OtpInput,
  ErrorText,
  ResendButtonBox,
  getResendButtonStyles,
} from './SmsVerify.styles';

import sparkTheme from '@/themes/sparkTheme';
import Button from '@/components/Button';
import { Footer } from '@/pages/Onboarding/components/Footer/Footer';
import { ConsentWidget } from '@/pages/Onboarding/components/ConsentWidget/ConsentWidget';
import { useConsent } from '@/hooks/useConsent';
import { useAcceptedLanguage } from '@/hooks/useAcceptedLanguage';
import { Header } from '@/pages/Onboarding/components/Header/Header';
import { Product } from '@/utils/productHelpers';
import { getProgram } from '@/utils/posthog';
import useBlockBackNavigation from '@/hooks/useBlockBackNavigation';
import { getSupabaseClient } from '@/utils/supabaseClient';
// TODO: SD-1899 when activate/confirm endpoint is live
// import { activateSparkConfirm } from '@/utils/sparkActivationApi';

interface SmsVerifyLocationState {
  phoneNumber?: string;
  agreeToReceiveTextMessages?: boolean;
  marketingConsent?: boolean;
  limbixUserId?: string;
}

const CODE_LENGTH = 6;
const INPUT_POSITIONS = [0, 1, 2, 3, 4, 5] as const;

const SmsVerifyInner: React.FC = () => {
  useAcceptedLanguage();
  useConsent();
  useBlockBackNavigation();

  const { t } = useTranslation();
  const theme = useTheme();
  const posthog = usePostHog();
  const { product } = useParams<{ product: Product }>();
  const { state: accountData } = useLocation<SmsVerifyLocationState>();
  const [code, setCode] = useState(Array(CODE_LENGTH).fill(''));
  const [error, setError] = useState('');
  const [isVerifying, setIsVerifying] = useState(false);
  const [isResending, setIsResending] = useState(false);
  const [resendSuccess, setResendSuccess] = useState(false);
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);
  const hasSubmittedRef = useRef(false);
  const program = getProgram(product);
  const isComplete = code.every((digit) => digit !== '');

  useEffect(() => {
    posthog?.capture('SparkSmsVerifyScreenViewed', { program });
  }, [posthog, program]);

  useEffect(() => {
    inputRefs.current[0]?.focus();
  }, []);

  // Verifies the OTP token against Supabase once the user has filled all 6 digits.
  const verifyOtpCode = useCallback(async (token: string) => {
    if (!accountData?.phoneNumber) { return; }

    setIsVerifying(true);
    setError('');

    const { data: verifyData, error: verifyError } = await getSupabaseClient().auth.verifyOtp({
      phone: accountData.phoneNumber,
      token,
      type: 'sms',
    });

    if (verifyError) {
      // Both invalid and expired OTP errors surface the same generic message per AC.
      setError(t('coverage.pre_onboarding.sms_verify.error_invalid'));
      setCode(Array(CODE_LENGTH).fill(''));
      hasSubmittedRef.current = false;
      setIsVerifying(false);
      return;
    }

    const supabaseUserId = verifyData?.user?.id;
    posthog?.capture('SparkSmsVerifySuccess', { program, supabase_user_id: supabaseUserId });

    // TODO: SD-1899 — Call Vercel route to flip user_app_entitlements.status → active.
    // await activateSparkConfirm({ supabaseUserId });

    // Hand control back to the Spark app via the deep link stored at flow entry.
    // Only follow custom-scheme deep links (e.g. spark://) — never http/https,
    // which would allow open redirect to an arbitrary website.
    const redirectTo = sessionStorage.getItem('spark_redirect_to');
    sessionStorage.removeItem('spark_redirect_to');

    if (redirectTo) {
      try {
        const { protocol } = new URL(redirectTo);
        if (protocol !== 'http:' && protocol !== 'https:') {
          window.location.href = redirectTo;
          return;
        }
      } catch {
        // Unparseable URL — fall through to reset below
      }
    }

    // Redirect did not fire (no redirectTo, blocked scheme, or unparseable URL).
    // Reset verifying state so the UI stays interactive.
    // TODO: SD-1899+ — navigate to Spark Onboarding route once confirmed.
    setIsVerifying(false);
  }, [accountData, t]);

  useEffect(() => {
    if (isComplete && !hasSubmittedRef.current) {
      hasSubmittedRef.current = true;
      posthog?.capture('SparkSmsVerifyCodeEntered', { program });
      verifyOtpCode(code.join(''));
    }
  }, [isComplete, code, posthog, program, accountData, verifyOtpCode]);

  const focusInput = (index: number) => inputRefs.current[index]?.focus();

  const resetState = () => {
    if (error) { setError(''); }
    if (resendSuccess) { setResendSuccess(false); }
    hasSubmittedRef.current = false;
  };

  const updateCode = (index: number, digit: string) => {
    const newCode = [...code];
    newCode[index] = digit;
    setCode(newCode);
    resetState();
  };

  const fillCode = (digits: string, startIndex: number) => {
    const newCode = [...code];
    digits.split('').forEach((digit, i) => {
      if (startIndex + i < CODE_LENGTH) {
        newCode[startIndex + i] = digit;
      }
    });
    setCode(newCode);
    resetState();
    const nextEmpty = newCode.findIndex((d) => !d);
    focusInput(nextEmpty === -1 ? CODE_LENGTH - 1 : nextEmpty);
  };

  const handleChange = (index: number, value: string) => {
    const digits = value.replace(/\D/g, '');
    // iOS SMS autofill may deliver the full OTP through onChange
    if (digits.length > 1) {
      fillCode(digits.slice(0, CODE_LENGTH), 0);
      return;
    }
    const digit = digits.slice(-1);
    updateCode(index, digit);
    if (digit && index < CODE_LENGTH - 1) { focusInput(index + 1); }
  };

  const handleKeyDown = (index: number, e: React.KeyboardEvent) => {
    if (e.key === 'Backspace' && !code[index] && index > 0) {
      focusInput(index - 1);
    }
  };

  const handlePaste = (index: number, e: React.ClipboardEvent) => {
    e.preventDefault();
    const digits = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, CODE_LENGTH);
    fillCode(digits, index);
  };

  // Resends the OTP by calling signInWithOtp again with the same phone number.
  const handleResendCode = async () => {
    if (isResending) { return; }

    posthog?.capture('SparkSmsVerifyResendClicked', { program });
    setError('');
    setResendSuccess(false);

    if (!accountData?.phoneNumber) { return; }

    setIsResending(true);
    try {
      const { error: resendError } = await getSupabaseClient().auth.signInWithOtp({
        phone: accountData.phoneNumber,
      });

      if (resendError) {
        setError(t('coverage.pre_onboarding.sms_verify.resend_error'));
        return;
      }

      setResendSuccess(true);
      setCode(Array(CODE_LENGTH).fill(''));
      hasSubmittedRef.current = false;
    } finally {
      setIsResending(false);
    }
  };

  return (
    <ContentContainer>
      <Header />
      <Container maxWidth="xs" style={{ flex: 1 }}>
        <HeaderBox>
          <Title variant="h2" color="textPrimary">
            {t('coverage.pre_onboarding.sms_verify.title')}
          </Title>
          <Typography color="textPrimary">
            {t('coverage.pre_onboarding.sms_verify.sub_title')}
          </Typography>
        </HeaderBox>

        <OtpInputContainer data-testid="sms-otp-input">
          {INPUT_POSITIONS.map((i) => (
            <OtpInput
              key={i}
              type="text"
              inputMode="numeric"
              maxLength={1}
              autoComplete="one-time-code"
              value={code[i]}
              onChange={(e) => handleChange(i, e.target.value)}
              onKeyDown={(e) => handleKeyDown(i, e)}
              onPaste={(e) => handlePaste(i, e)}
              ref={(el) => { inputRefs.current[i] = el; }}
              data-testid={`otp-input-${i}`}
              className={error ? 'error' : ''}
              disabled={isVerifying}
              aria-label={t('coverage.pre_onboarding.sms_verify.input_aria_label', { position: i + 1 })}
            />
          ))}
        </OtpInputContainer>

        {error && <ErrorText data-testid="error-message">{error}</ErrorText>}

        <ResendButtonBox>
          <Button
            variant="contained"
            data-testid="resend-code-button"
            type="button"
            fullWidth
            style={getResendButtonStyles(theme)}
            onClick={handleResendCode}
            disabled={isVerifying || isResending}
          >
            {t('coverage.pre_onboarding.sms_verify.resend_code')}
          </Button>

          {resendSuccess && (
            <Typography
              color="textPrimary"
              data-testid="resend-success-message"
              style={{ marginTop: 8, fontSize: 14 }}
            >
              {t('coverage.pre_onboarding.sms_verify.resend_success')}
            </Typography>
          )}
        </ResendButtonBox>

        <ConsentWidget />
      </Container>
      <Footer />
    </ContentContainer>
  );
};

const SmsVerify: React.FC = () => (
  <ThemeProvider theme={sparkTheme}>
    <SmsVerifyInner />
  </ThemeProvider>
);

export default SmsVerify;
